/**
 * Production Data Validation
 * 
 * Ensures data integrity for production entries.
 * Oil companies audit this data for royalty payments - accuracy is critical.
 */

// ============================================================================
// VALIDATION RULES - Based on real-world oil field constraints
// ============================================================================

export interface ValidationRule {
  min: number;
  max: number;
  decimals: number;
  unit: string;
  warningThreshold?: { low?: number; high?: number };
  description: string;
}

export const PRODUCTION_VALIDATION_RULES: Record<string, ValidationRule> = {
  // Volume measurements (barrels)
  oilVolume: {
    min: 0,
    max: 10000, // Most wells produce < 1000 bbl/day, exceptional wells < 10000
    decimals: 2,
    unit: 'BBL',
    warningThreshold: { high: 500 }, // Flag unusually high values for review
    description: 'Oil volume in barrels'
  },
  waterVolume: {
    min: 0,
    max: 50000, // Water can be much higher than oil
    decimals: 2,
    unit: 'BBL',
    warningThreshold: { high: 1000 },
    description: 'Water volume in barrels'
  },
  totalSales: {
    min: 0,
    max: 10000,
    decimals: 2,
    unit: 'BBL',
    warningThreshold: { high: 500 },
    description: 'Total sales in barrels'
  },
  
  // Gas measurements (MCF = thousand cubic feet)
  gasVolume: {
    min: 0,
    max: 100000, // Gas volumes can be very high
    decimals: 2,
    unit: 'MCF',
    warningThreshold: { high: 5000 },
    description: 'Gas volume in MCF'
  },
  
  // Pressure measurements (PSI)
  casingPressure: {
    min: 0,
    max: 15000, // Deep wells can have very high pressures
    decimals: 0,
    unit: 'PSI',
    warningThreshold: { high: 5000 },
    description: 'Casing pressure in PSI'
  },
  tubingPressure: {
    min: 0,
    max: 15000,
    decimals: 0,
    unit: 'PSI',
    warningThreshold: { high: 5000 },
    description: 'Tubing pressure in PSI'
  },
  flowlinePressure: {
    min: 0,
    max: 5000,
    decimals: 0,
    unit: 'PSI',
    warningThreshold: { high: 1000 },
    description: 'Flowline pressure in PSI'
  },
  
  // Tank gauge measurements (inches)
  tankGauge: {
    min: 0,
    max: 500, // Tank height in inches (standard tanks ~384" / 32ft)
    decimals: 2,
    unit: 'inches',
    description: 'Tank gauge reading in inches'
  },
  kolorKutWater: {
    min: 0,
    max: 500, // Can't exceed tank height
    decimals: 2,
    unit: 'inches',
    description: 'Water cut measurement in inches'
  },
  
  // Pump metrics
  strokesPerMinute: {
    min: 0,
    max: 30, // Typical range 4-15 SPM
    decimals: 1,
    unit: 'SPM',
    warningThreshold: { low: 2, high: 20 },
    description: 'Pump strokes per minute'
  },
  motorAmps: {
    min: 0,
    max: 500,
    decimals: 1,
    unit: 'Amps',
    warningThreshold: { high: 200 },
    description: 'Motor amperage'
  },
  
  // Financial
  maintenanceCost: {
    min: 0,
    max: 1000000, // Large repairs can be expensive
    decimals: 2,
    unit: '$',
    warningThreshold: { high: 10000 },
    description: 'Maintenance cost in dollars'
  },
  
  // Temperature
  temperature: {
    min: -50, // Cold weather operations
    max: 300, // High temp for produced fluids
    decimals: 1,
    unit: '°F',
    description: 'Temperature in Fahrenheit'
  }
};

// ============================================================================
// VALIDATION RESULT TYPES
// ============================================================================

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  warning?: string;
  sanitizedValue: number | null;
}

export interface FieldValidationState {
  value: string;
  numericValue: number | null;
  isValid: boolean;
  error?: string;
  warning?: string;
  isTouched: boolean;
}

// ============================================================================
// CORE VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validates and sanitizes a numeric production value
 */
export function validateProductionValue(
  value: string | number | null | undefined,
  fieldType: keyof typeof PRODUCTION_VALIDATION_RULES
): ValidationResult {
  const rule = PRODUCTION_VALIDATION_RULES[fieldType];
  
  if (!rule) {
    return {
      isValid: false,
      error: `Unknown field type: ${fieldType}`,
      sanitizedValue: null
    };
  }
  
  // Handle empty/null values - these are valid (optional field)
  if (value === null || value === undefined || value === '') {
    return {
      isValid: true,
      sanitizedValue: null
    };
  }
  
  // Convert to string for parsing
  const strValue = String(value).trim();
  
  // Check for non-numeric characters (except decimal point and minus for negatives)
  if (!/^-?\d*\.?\d*$/.test(strValue)) {
    return {
      isValid: false,
      error: `Invalid number format. Enter numbers only.`,
      sanitizedValue: null
    };
  }
  
  // Parse the number
  const numValue = parseFloat(strValue);
  
  // Check if it's actually a number
  if (isNaN(numValue)) {
    return {
      isValid: false,
      error: `Invalid number`,
      sanitizedValue: null
    };
  }
  
  // Check minimum
  if (numValue < rule.min) {
    return {
      isValid: false,
      error: `Value cannot be less than ${rule.min} ${rule.unit}`,
      sanitizedValue: null
    };
  }
  
  // Check maximum
  if (numValue > rule.max) {
    return {
      isValid: false,
      error: `Value cannot exceed ${rule.max.toLocaleString()} ${rule.unit}`,
      sanitizedValue: null
    };
  }
  
  // Round to allowed decimals
  const multiplier = Math.pow(10, rule.decimals);
  const sanitizedValue = Math.round(numValue * multiplier) / multiplier;
  
  // Check for warnings (unusual but not invalid values)
  let warning: string | undefined;
  if (rule.warningThreshold) {
    if (rule.warningThreshold.low !== undefined && sanitizedValue < rule.warningThreshold.low && sanitizedValue > 0) {
      warning = `Unusually low value (${sanitizedValue} ${rule.unit}). Please verify.`;
    }
    if (rule.warningThreshold.high !== undefined && sanitizedValue > rule.warningThreshold.high) {
      warning = `Unusually high value (${sanitizedValue.toLocaleString()} ${rule.unit}). Please verify.`;
    }
  }
  
  return {
    isValid: true,
    warning,
    sanitizedValue
  };
}

/**
 * Validates tank gauge readings with cross-field logic
 */
export function validateTankGauges(
  topGauge: string | number,
  bottomGauge: string | number,
  tankCapacityInches?: number
): ValidationResult {
  const topResult = validateProductionValue(topGauge, 'tankGauge');
  const bottomResult = validateProductionValue(bottomGauge, 'tankGauge');
  
  // Return first error
  if (!topResult.isValid) return topResult;
  if (!bottomResult.isValid) return bottomResult;
  
  const top = topResult.sanitizedValue;
  const bottom = bottomResult.sanitizedValue;
  
  // If both are empty, that's valid
  if (top === null && bottom === null) {
    return { isValid: true, sanitizedValue: null };
  }
  
  // If only one is filled, that's okay (partial entry)
  if (top === null || bottom === null) {
    return { isValid: true, sanitizedValue: top ?? bottom };
  }
  
  // Top should be >= bottom (top is distance from top of tank, so lower number = more full)
  // Actually in oil field: top gauge is usually the "liquid level from top" so smaller = more full
  // Bottom gauge is the "bottom of fluid" reading
  // Net = top - bottom (inches of fluid)
  
  if (bottom > top) {
    return {
      isValid: false,
      error: 'Bottom gauge cannot be greater than top gauge',
      sanitizedValue: null
    };
  }
  
  // Check against tank capacity if provided
  if (tankCapacityInches && top > tankCapacityInches) {
    return {
      isValid: false,
      error: `Gauge reading (${top}") exceeds tank height (${tankCapacityInches}")`,
      sanitizedValue: null
    };
  }
  
  const netInches = top - bottom;
  return {
    isValid: true,
    sanitizedValue: netInches,
    warning: netInches > 300 ? 'Very high fluid level. Please verify.' : undefined
  };
}

/**
 * Validates Kolor Kut water cut reading
 */
export function validateKolorKut(
  waterCutInches: string | number,
  totalFluidInches?: number
): ValidationResult {
  const result = validateProductionValue(waterCutInches, 'kolorKutWater');
  
  if (!result.isValid) return result;
  if (result.sanitizedValue === null) return result;
  
  // Water cut can't exceed total fluid in tank
  if (totalFluidInches !== undefined && result.sanitizedValue > totalFluidInches) {
    return {
      isValid: false,
      error: `Water level (${result.sanitizedValue}") cannot exceed total fluid (${totalFluidInches}")`,
      sanitizedValue: null
    };
  }
  
  return result;
}

// ============================================================================
// BATCH VALIDATION FOR FORM SUBMISSION
// ============================================================================

export interface ProductionEntryValidation {
  gasVolume?: string;
  casingPressure?: string;
  tubingPressure?: string;
  totalSales?: string;
  maintenanceCost?: string;
  tankReadings?: Array<{
    tankId: string;
    topGauge: string;
    bottomGauge: string;
    kolorKut?: { waterCutInches: string };
  }>;
}

export interface ValidationErrors {
  [fieldName: string]: string;
}

export interface ValidationWarnings {
  [fieldName: string]: string;
}

export interface BatchValidationResult {
  isValid: boolean;
  errors: ValidationErrors;
  warnings: ValidationWarnings;
  sanitizedData: Record<string, number | null>;
}

/**
 * Validates all fields in a production entry form
 */
export function validateProductionEntry(data: ProductionEntryValidation): BatchValidationResult {
  const errors: ValidationErrors = {};
  const warnings: ValidationWarnings = {};
  const sanitizedData: Record<string, number | null> = {};
  
  // Validate scalar fields
  const scalarFields: Array<[string, keyof typeof PRODUCTION_VALIDATION_RULES]> = [
    ['gasVolume', 'gasVolume'],
    ['casingPressure', 'casingPressure'],
    ['tubingPressure', 'tubingPressure'],
    ['totalSales', 'totalSales'],
    ['maintenanceCost', 'maintenanceCost'],
  ];
  
  for (const [fieldName, ruleKey] of scalarFields) {
    const value = data[fieldName as keyof ProductionEntryValidation];
    if (typeof value === 'string' || typeof value === 'number') {
      const result = validateProductionValue(value, ruleKey);
      if (!result.isValid && result.error) {
        errors[fieldName] = result.error;
      }
      if (result.warning) {
        warnings[fieldName] = result.warning;
      }
      sanitizedData[fieldName] = result.sanitizedValue;
    }
  }
  
  // Validate tank readings
  if (data.tankReadings) {
    for (const tank of data.tankReadings) {
      const tankKey = `tank_${tank.tankId}`;
      
      // Validate top gauge
      const topResult = validateProductionValue(tank.topGauge, 'tankGauge');
      if (!topResult.isValid && topResult.error) {
        errors[`${tankKey}_top`] = topResult.error;
      }
      
      // Validate bottom gauge
      const bottomResult = validateProductionValue(tank.bottomGauge, 'tankGauge');
      if (!bottomResult.isValid && bottomResult.error) {
        errors[`${tankKey}_bottom`] = bottomResult.error;
      }
      
      // Cross-validate gauges
      if (topResult.isValid && bottomResult.isValid) {
        const gaugeResult = validateTankGauges(tank.topGauge, tank.bottomGauge);
        if (!gaugeResult.isValid && gaugeResult.error) {
          errors[`${tankKey}_gauges`] = gaugeResult.error;
        }
        if (gaugeResult.warning) {
          warnings[`${tankKey}_gauges`] = gaugeResult.warning;
        }
      }
      
      // Validate Kolor Kut if present
      if (tank.kolorKut?.waterCutInches) {
        const kkResult = validateProductionValue(tank.kolorKut.waterCutInches, 'kolorKutWater');
        if (!kkResult.isValid && kkResult.error) {
          errors[`${tankKey}_kolorKut`] = kkResult.error;
        }
        if (kkResult.warning) {
          warnings[`${tankKey}_kolorKut`] = kkResult.warning;
        }
      }
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    warnings,
    sanitizedData
  };
}

// ============================================================================
// INPUT SANITIZATION HELPERS
// ============================================================================

/**
 * Sanitizes input as user types - removes invalid characters
 */
export function sanitizeNumericInput(value: string, allowDecimal: boolean = true, allowNegative: boolean = false): string {
  let sanitized = value;
  
  // Remove any character that's not a digit, decimal point, or minus sign
  if (allowNegative && allowDecimal) {
    sanitized = value.replace(/[^\d.-]/g, '');
  } else if (allowDecimal) {
    sanitized = value.replace(/[^\d.]/g, '');
  } else if (allowNegative) {
    sanitized = value.replace(/[^\d-]/g, '');
  } else {
    sanitized = value.replace(/[^\d]/g, '');
  }
  
  // Only allow one decimal point
  const parts = sanitized.split('.');
  if (parts.length > 2) {
    sanitized = parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Only allow minus at the start
  if (allowNegative) {
    const hasLeadingMinus = sanitized.startsWith('-');
    sanitized = sanitized.replace(/-/g, '');
    if (hasLeadingMinus) {
      sanitized = '-' + sanitized;
    }
  }
  
  return sanitized;
}

/**
 * Formats a number for display with appropriate decimals
 */
export function formatProductionValue(
  value: number | null | undefined,
  fieldType: keyof typeof PRODUCTION_VALIDATION_RULES
): string {
  if (value === null || value === undefined) return '';
  
  const rule = PRODUCTION_VALIDATION_RULES[fieldType];
  if (!rule) return String(value);
  
  return value.toFixed(rule.decimals);
}

/**
 * Parses a string to number, returning null for empty/invalid
 */
export function parseProductionValue(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? null : parsed;
}
